import { Deliverable } from '@app/features/scoping/models/deliverable.model'
import { Department } from '@app/features/scoping/models/department.model'
import { FeeAmountType, FeeItemInstance } from '@app/features/scope-overview/model/fee-item.model'
import { Money } from '@app/features/scope-overview/model/money.model'
import { ThirdPartyCost } from '@app/core/model/third-party-cost.model'
import { Type } from 'class-transformer'
import { User } from '@core/model/user.model'
import { UserComment } from '@core/model/comment.model'

export class ScopeComponent {
  id!: number
  name?: string
  description?: string
  extraDescription?: string
  internalNote?: string
  complexity!: string
  quantity!: number
  @Type(() => User) createdBy!: User
  @Type(() => Date) createdTs!: Date
  currencyUnit!: string
  @Type(() => Deliverable) deliverable!: Deliverable
  @Type(() => FeeItemInstance) feeItems!: FeeItemInstance[]
  fixedPricing!: boolean
  fixedFeeEditable!: boolean
  departmentsInitialised!: boolean
  markup!: number
  @Type(() => Money) naturalSellingPrice!: Money
  @Type(() => Money) fixedCost!: Money
  @Type(() => Department) departments!: Department[]
  @Type(() => ThirdPartyCost) thirdPartyCosts!: ThirdPartyCost[]
  order!: number
  prediction!: { riskIndicator: string; stDev: number; sampleSize: number; likelihood: number; source: string }
  rateCardTotalHours!: number
  cachedRatecardHours!: number
  rateCardTotalMinutes!: number
  @Type(() => Money) rateCardTotalResourceSellingPrice!: Money
  scopeMarkTotalHours!: number
  @Type(() => Money) scopeMarkTotalSellingPrice!: Money
  source!: {
    description: string
    foreignSource: any
    id: number
    name: string
    nonTranslatedDescription: string
    nonTranslatedName: string
    parentId: number
    scopeType: string
  }
  sourceType!: string
  @Type(() => Money) totalCostPrice!: Money
  @Type(() => Money) totalProfit!: Money
  @Type(() => Money) totalRateCardSellingPrice!: Money
  @Type(() => Money) totalSellingPrice!: Money
  trafficSyncStatus!: string
  @Type(() => Money) unitCostPrice!: Money
  @Type(() => Money) unitProfit!: Money
  @Type(() => Money) unitSellingPrice!: Money
  @Type(() => User) updatedBy!: User
  @Type(() => Date) updatedTs!: Date
  breadcrumb?: string
  section: any
  @Type(() => UserComment) comments!: UserComment[]
  costPlusValue: number
  @Type(() => Date) startDate: Date
  @Type(() => Date) endDate: Date
  updatedName = ''
  costAmount: number
  priceModifierFormula?: string
  quantityModifierFormula?: string
  priceModifier?: number
  quantityModifier?: number

  getTotalRateCardHours() {
    if (!this.departmentsInitialised && this.cachedRatecardHours != null) {
      return this.cachedRatecardHours
    }
    let total = 0
    for (let i = 0; i < this.departments.length; i++) {
      const deptTotal = this.departments[i].getTotalRateCardHours()
      if (isNaN(deptTotal)) {
        return null
      }
      total += deptTotal
    }
    return total * this.quantity * this.quantityModifier
  }

  totalScopeMarkSellingPrice() {
    let total = new Money(0, this.currencyUnit)
    for (let i = 0; i < this.departments.length; i++) {
      const department = this.departments[i]
      const price = department.totalScopeMarkSellingPrice()
      if (price == null) {
        return null
      }
      total = total.add(price)
    }

    return total.multiply(this.quantity).multiply(this.quantityModifier).multiply(this.priceModifier)
  }

  getTotalSellingPrice(ctx = { excludeThirdPartyCosts: false, excludeComponentFees: false }) {
    let total = new Money(0, this.currencyUnit)
    if (this.fixedPricing && this.fixedCost != null) {
      total = new Money(this.fixedCost.amount, this.fixedCost.currency)
      total = total.multiply(this.quantity).multiply(this.quantityModifier).multiply(this.priceModifier)
    } else {
      for (let i = 0; i < this.departments.length; i++) {
        const department = this.departments[i]
        const price = department.getTotalSellingPrice({
          ...ctx,
          markup: this.costPlusValue ? this.costPlusValue : null,
        })
        if (price == null) return null
        total = total.add(price)
      }
      total = total.multiply(this.quantity).multiply(this.quantityModifier).multiply(this.priceModifier)
    }

    if (!ctx.excludeThirdPartyCosts && !this.fixedPricing) {
      total = total.add(this.thirdPartyCostTotal())
    }

    if (!ctx.excludeComponentFees && !this.fixedPricing) {
      total = total.addValue(this.getTotalFees(total.amount))
    }

    return total
  }

  thirdPartyCostTotal() {
    if (this.thirdPartyCosts && this.thirdPartyCosts.length) {
      return this.thirdPartyCosts.map((tpc) => tpc.calculateSellingPrice()).reduce((acc, next) => acc.add(next))
    }
    return new Money(0, this.currencyUnit)
  }

  getTotalCostPrice() {
    let total = new Money(0, this.currencyUnit)
    if (this.fixedPricing && this.costAmount) {
      total = total.add(new Money(this.costAmount, this.currencyUnit))
    }
    for (let i = 0; i < this.departments.length; i++) {
      const department = this.departments[i]
      const price = department.getTotalCostPrice()
      if (price == null) {
        return null
      }
      total = total.add(price)
    }
    total = total.multiply(this.quantity).multiply(this.quantityModifier).multiply(this.priceModifier)

    this.thirdPartyCosts.forEach(function (tpc) {
      total = total.add(tpc.calculateCostPrice())
    })

    return new Money(total.getValue(), total.getCurrencyCode())
  }

  getMarkup() {
    const sellingPrice = this.getTotalSellingPrice({ excludeThirdPartyCosts: false, excludeComponentFees: true })
    const totalRateCardCost = this.getTotalCostPrice()
    if (totalRateCardCost == null) {
      return null
    }
    const cost = totalRateCardCost
    if (cost.amount == 0) {
      return 100
    }
    if (sellingPrice && (cost.amount || 0) > 0) {
      const grossProfit = sellingPrice.amount - (cost.amount || 0)
      const markup = (grossProfit / (cost.amount || 0)) * 100
      return parseFloat(markup.toFixed(2))
    }
    return 0
  }

  getTotalFees(amount: number | null = null) {
    let total = 0
    if (this.feeItems?.length !== 0) {
      if (amount == null && this.feeItems.find((fee) => fee.feeItem.amountType !== FeeAmountType.MONETARY)) {
        amount = this.getTotalSellingPrice({ excludeThirdPartyCosts: false, excludeComponentFees: true })?.amount
      }
      for (let i = 0; i < this.feeItems.length; i++) {
        total += Number(this.getFeePrice(this.feeItems[i], amount))
      }
    }
    return total
  }

  getFeePrice(fee: FeeItemInstance, amount: number): number {
    if (fee.feeItem.amountType === FeeAmountType.MONETARY) {
      return parseFloat(fee.feeItem.amount.toFixed(2))
    } else {
      return (Number(amount) * Number(fee.feeItem.amount)) / 100
    }
  }

  hasCostPlus(): boolean {
    return (
      (this.costPlusValue && !this.fixedPricing) ||
      (this.costPlusValue && this.fixedPricing && this.departments.length > 0)
    )
  }

  getUpdatedName() {
    return this.updatedName
  }

  setUpdatedName(name) {
    return (this.updatedName = name)
  }

  getBreadcrumb() {
    return this.breadcrumb
  }

  getCommentsCount() {
    const ownCommentsCount = this.comments.length
    let departmentComments = 0
    for (let i = 0; i < this.departments.length; i++) {
      departmentComments += this.departments[i].getCommentsCount()
    }
    let tpcCommentsCount = 0
    for (let j = 0; j < this.thirdPartyCosts.length; j++) {
      tpcCommentsCount += this.thirdPartyCosts[j].getCommentsCount()
    }
    return ownCommentsCount + departmentComments + tpcCommentsCount
  }
}
